import { Input, useSnackbar } from '@components/common';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import Button from '@components/Button';
import { media } from 'styled-bootstrap-grid';
import { api } from 'helpers/auth-axios';

interface ErrorMessage {
    message: string;
}

const CustomerModal = ({
    onClose,
    onSave,
    title,
    onEdit,
    editModel,
}: {
    onClose(): void;
    onSave(data: any): void;
    title?: any;
    onEdit?: any;
    editModel?: any;
}) => {
    const [openSnackbar] = useSnackbar();
    const [loading, setLoading] = useState<any>(false);

    // form field state variables
    const [firstName, setFirstName] = useState<string>(editModel ? onEdit.firstname : '');
    const [lastName, setLastName] = useState<string>(editModel ? onEdit.lastname : '');
    const [email, setEmail] = useState<string>(editModel ? onEdit.email : '');
    const [phone, setPhone] = useState<string>(editModel ? onEdit.phone : '');

    // form field error message state variables
    const [firstNameFieldErrorMessage, setFirstNameFieldErrorMessage] = useState<
        ErrorMessage | undefined
    >();
    const [lastNameFieldErrorMessage, setLastNameFieldErrorMessage] = useState<
        ErrorMessage | undefined
    >();
    const [emailFieldErrorMessage, setEmailFieldErrorMessage] = useState<
        ErrorMessage | undefined
    >();
    const [phoneFieldErrorMessage, setPhoneFieldErrorMessage] = useState<
        ErrorMessage | undefined
    >();

    const [profileInfoChanged, setProfileInfoChanged] = useState<boolean>(false);

    useEffect(() => {
        if (editModel) {
            if (!onEdit) return;
            // If all fields are set and any changed, set to true
            setProfileInfoChanged(
                (firstName.trim() !== '' && firstName !== onEdit?.firstname) ||
                    (lastName.trim() !== '' && lastName !== onEdit?.lastname) ||
                    (email.trim() !== '' &&
                        isValidEmailAddress(email) &&
                        email !== onEdit?.email) ||
                    (phone.trim() !== '' && phone.length === 12 && phone !== onEdit?.phone)
            );
        } else {
            // Set to true if all fields are set and not empty strings
            setProfileInfoChanged(
                firstName.trim() !== '' &&
                    lastName.trim() !== '' &&
                    email.trim() !== '' &&
                    isValidEmailAddress(email) &&
                    phone.trim() !== '' &&
                    phone.length === 12
            );
        }
    }, [editModel, onEdit, firstName, lastName, email, phone]);

    const closeModal = (event: any) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const onSubmit = () => {
        // Make sure first name is entered
        if (firstName.trim() === '') {
            setFirstNameFieldErrorMessage({
                message: 'First name is required.',
            });
            return;
        }
        // Make sure last name is entered
        if (lastName.trim() === '') {
            setLastNameFieldErrorMessage({
                message: 'Last name is required.',
            });
            return;
        }
        // Make sure email is proper format
        if (email === '') {
            setEmailFieldErrorMessage({
                message: 'Email is required.',
            });
            return;
        } else if (!isValidEmailAddress(email)) {
            setEmailFieldErrorMessage({
                message: 'Must enter a valid email address.',
            });
            return;
        }
        // Make sure phone number exists and is in proper format
        const phoneNumber = formatPhoneNumberString(phone);
        setPhone(phoneNumber);
        if (phoneNumber.trim() === '') {
            setPhoneFieldErrorMessage({
                message: 'A phone number is required.',
            });
            return;
        } else if (phoneNumber.length !== 12) {
            setPhoneFieldErrorMessage({
                message: 'A phone number is too short.',
            });
            return;
        }

        editModel ? updateCustomer() : createCustomer();
    };

    const createCustomer = () => {
        setLoading(true);

        let requestBody: any = {
            firstname: firstName,
            lastname: lastName,
            email,
            phone,
        };

        api.post('/customers', requestBody)
            .then((res: any) => {
                setLoading(false);

                onSave(res?.data);
                openSnackbar('Customer added sucessfully');
                onClose();
            })
            .catch((e: any) => {
                setLoading(false);

                openSnackbar(e?.response?.data?.message);
            });
    };

    const updateCustomer = () => {
        if (!onEdit) return;
        setLoading(true);

        let requestBody: any = {
            firstname: firstName,
            lastname: lastName,
            email,
            phone,
        };

        api.put(`/customers/${onEdit?._id}`, requestBody)
            .then((res: any) => {
                setLoading(false);

                if (res) {
                    onSave(res?.data);
                    openSnackbar('Customer updated sucessfully');
                    onClose();
                }
            })
            .catch((e: any) => {
                setLoading(false);

                openSnackbar(e?.response?.data?.message);
            });
    };

    /*
     * Utility Functions
     */

    const formatPhoneNumberString = (phoneNumber: string) => {
        let phone = phoneNumber;
        if (phone.length <= 3) {
            phone = phone.replace(/[- .]/g, '');
        } else if (phone.length <= 7) {
            phone = phone.replace(/[- .]/g, '');
            phone = phone.slice(0, 3) + '-' + phone.slice(3, 6);
        } else if (phone.length >= 7) {
            phone = phone.replace(/[- .]/g, '');
            phone = phone.slice(0, 3) + '-' + phone.slice(3, 6) + '-' + phone.slice(6);
        }
        return phone;
    };

    const isValidEmailAddress = (emailAddr: string) => {
        const reg =
            /^([\w\!\#$\%\&\'\*\+\-\/\=\?\^\`{\|\}\~]+\.)*[\w\!\#$\%\&\'\*\+\-\/\=\?\^\`{\|\}\~]+@((((([a-z0-9]{1}[a-z0-9\-]{0,62}[a-z0-9]{1})|[a-z])\.)+[a-z]{2,6})|(\d{1,3}\.){3}\d{1,3}(\:\d{1,5})?)$/i;
        const val = reg.test(emailAddr);
        return val;
    };

    return (
        <Model tabIndex={-1} onClick={closeModal}>
            <ModelBody style={{ width: '680px' }}>
                <ModelHead>
                    <Cover onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </Cover>
                </ModelHead>
                <ModelHeading>
                    <Heading>{title}</Heading>
                </ModelHeading>
                <>
                    <ModelContent>
                        <CustomDuv>
                            <InputGroup>
                                <Input
                                    label="First Name"
                                    name="firstname"
                                    value={firstName}
                                    onChange={({ value }: { value: string }) => {
                                        setFirstNameFieldErrorMessage(undefined);
                                        const firstname = value.trim();
                                        setFirstName(firstname);
                                        if (firstname === '') {
                                            setFirstNameFieldErrorMessage({
                                                message: 'First name is required.',
                                            });
                                        }
                                    }}
                                    error={firstNameFieldErrorMessage}
                                />
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    label="Last Name"
                                    name="lastname"
                                    value={lastName}
                                    onChange={({ value }: { value: string }) => {
                                        setLastNameFieldErrorMessage(undefined);
                                        const lastname = value.trim();
                                        setLastName(lastname);
                                        if (lastname === '') {
                                            setLastNameFieldErrorMessage({
                                                message: 'Last name is required.',
                                            });
                                        }
                                    }}
                                    error={lastNameFieldErrorMessage}
                                />
                            </InputGroup>
                        </CustomDuv>
                        <CustomDuv>
                            <InputGroup>
                                <Input
                                    label="Email"
                                    name="email"
                                    value={email}
                                    onChange={({ value }: { value: string }) => {
                                        setEmailFieldErrorMessage(undefined);
                                        const _email = value.trim();
                                        setEmail(_email);
                                        if (_email === '') {
                                            setEmailFieldErrorMessage({
                                                message: 'Email is required.',
                                            });
                                        } else if (!isValidEmailAddress(_email)) {
                                            setEmailFieldErrorMessage({
                                                message: 'Must enter a valid email address.',
                                            });
                                        }
                                    }}
                                    error={emailFieldErrorMessage}
                                />
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    allowPhoneNumberOnly={true}
                                    maxLength={12}
                                    label="Phone"
                                    name="phone"
                                    value={phone}
                                    onChange={({ value }: { value: string }) => {
                                        setPhoneFieldErrorMessage(undefined);
                                        const phoneNumber = formatPhoneNumberString(value);
                                        setPhone(phoneNumber);
                                        if (phoneNumber.trim() === '') {
                                            setPhoneFieldErrorMessage({
                                                message: 'A phone number is required.',
                                            });
                                        } else if (phoneNumber.length !== 12) {
                                            setPhoneFieldErrorMessage({
                                                message: 'Phone number is too short.',
                                            });
                                        }
                                    }}
                                    error={phoneFieldErrorMessage}
                                />
                            </InputGroup>
                        </CustomDuv>
                    </ModelContent>

                    <Footer>
                        {profileInfoChanged && (
                            <Button
                                width="100% !important"
                                bgtype={'secondary'}
                                type={'submit'}
                                disabled={loading}
                                label="Save"
                                ifClicked={onSubmit}></Button>
                        )}
                    </Footer>
                </>
            </ModelBody>
        </Model>
    );
};
const Model = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    background-color: rgba(0, 0, 0, 0.5);
    padding: 24px;
    overflow: auto;
    overscroll-behavior-y: contain;
    z-index: 102;
`;
const ModelBody = styled.div`
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 15px 25px -6px rgb(0 0 0 / 10%);
    padding: 24px;
    max-width: 82vw;
`;
const ModelHead = styled.div`
    display: flex;
    justify-content: end;
    margin-bottom: 0.75rem;
`;
const Cover = styled.div`
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3f4254;
    background-color: #e4e6ef;
    border-radius: 8px;
    font-size: 1.2rem;
    cursor: pointer;
`;
const ModelHeading = styled.div`
    align-items: center !important;
    display: flex !important;
`;
const Heading = styled.h1`
    font-weight: 600 !important;
    font-size: 1.875rem !important;
    line-height: 2.25rem !important;
    margin-bottom: 0px !important;
`;
const ModelContent = styled.div`
    padding: 2rem 0 1rem 0 !important;
    color: rgba(157, 160, 164) !important;
`;
const InputGroup = styled.div`
    margin-bottom: 1.71rem;
    width: 100%;
`;
const Footer = styled.div`
    display: flex;
    justify-content: space-between;
`;
const DaysName = styled.div`
    display: flex;
    flex-direction: row;
`;
const Options = styled.div<any>`
    margin-top: 0.5rem;
    height: 2rem;
    width: 2rem;
    border: 1px solid #e5e7eb;
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background-color: ${({ active }: any) => (active ? `rgb(57, 57, 185)` : 'white')};

    color: ${({ active }: any) => (active ? `white` : 'rgb(57, 57, 185)')};
`;
const CustomDuv = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    ${media.xs`
    display: block;
    `}
    ${media.sm`
      display: block;
    `}
      ${media.md`
      display: flex;
    `}
    ${media.lg`
      display: flex;
    `}
`;
export default CustomerModal;
